import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithRef, ReactNode } from "react";

import { cn } from "../lib/cn";
import { Spinner } from "./spinner";

/**
 * Bouton Natif SOTA — Style macOS / iOS / visionOS / Apple HIG
 *
 * Principes visuels natifs :
 * - Liseré spéculaire blanc supérieur unifié : utilise la même propriété `inset 0 1px 0 0 rgba(255,255,255, ALPHA)`
 *   avec une opacité strictement croissante (none=0%, subtle=15%, medium=30%, strong=50%).
 * - Dégradé de profondeur synchronisé sur le même axe `relief`.
 * - Anti-web jitter : aucun zoom/scale du bouton au clic (stabilité géométrique 100%).
 * - Micro-animations fluides sur les icônes au survol.
 *
 * ── Accélération matérielle ─────────────────────────────────────────────────
 * L'enfoncement au clic est rendu par `brightness()` et `opacity`, deux
 * propriétés que le compositeur applique sans repasser par le peintre : un
 * bouton reste fluide même pendant qu'un rendu React occupe le fil principal.
 * `box-shadow` est hors de la liste des transitions — le relief spéculaire est
 * statique, et l'ombre de survol de la variante `floating` passe par `lift`,
 * qui fait varier une opacité au lieu de repeindre une ombre.
 */
const buttonVariants = cva(
  [
    "group/button relative inline-flex items-center justify-center gap-2 whitespace-nowrap select-none touch-manipulation cursor-pointer",
    "rounded-lg font-medium tracking-tight transform-gpu",
    // Zone tactile minimale de 44px sur mobile via pseudo-élément invisible
    "before:absolute before:-inset-2 before:content-[''] sm:before:hidden",
    "transition-[background-color,border-color,color,opacity,filter] duration-fast ease-standard",
    // Physique d'enfoncement natif OS : pas de scale(0.97) web jitter, stabilité géométrique absolue
    "active:transition-none active:brightness-95 active:opacity-90",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-1 focus-visible:ring-offset-surface",
    "disabled:pointer-events-none disabled:opacity-45 disabled:shadow-none",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:transform-gpu [&_svg]:transition-transform [&_svg]:duration-normal [&_svg]:ease-standard group-hover/button:[&_svg]:scale-105",
  ],
  {
    variants: {
      variant: {
        /** Bouton d'action principale natif. */
        primary: "text-fg-on-accent border border-accent-hover/40 hover:brightness-105",

        /** Bouton système natif macOS/iOS (Push Button). */
        secondary: "text-fg border border-border/80 hover:bg-surface-sunken hover:border-border-strong",

        /** Bouton dépoli verre translucide natif (Toolbar / visionOS / macOS Floating). */
        glass: "backdrop-blur-md text-fg border border-border/60 hover:bg-surface-raised",

        /** Bouton flottant surélevé. L'ombre haute est pré-peinte et fondue par `lift`. */
        floating:
          "lift [--nx-lift-shadow:var(--shadow-lg)] text-fg border border-border/80 shadow-md hover:border-border-strong",

        /** Bouton contour fin natif. */
        outline: "border border-border-strong/70 text-fg hover:bg-surface-raised hover:border-border-strong",

        /** Bouton sans fond (Ghost natif pour toolbars & icônes). */
        ghost: "text-fg-muted hover:bg-surface-sunken/80 hover:text-fg",

        /** Bouton teinté doux (iOS Tinted / visionOS Segment). */
        subtle: "bg-accent-subtle/80 text-accent-fg border border-accent/20 hover:bg-accent-subtle",

        /** Bouton d'action destructive (Micro-dégradé danger). */
        danger: "text-fg-on-accent border border-danger-hover/40 hover:brightness-105",

        /** Lien texte natif. */
        link: "text-accent-fg underline-offset-4 hover:underline active:opacity-75",
      },
      relief: {
        /** Plat : 0% liseré blanc. */
        none: "[box-shadow:none]",
        /** Subtil (DÉFAUT) : Liseré blanc spéculaire 15% discret et élégant. */
        subtle: "shadow-xs [box-shadow:inset_0_1px_0_0_rgba(255,255,255,0.15)]",
        /** Moyen : Liseré blanc spéculaire 30% bien visible. */
        medium: "shadow-xs [box-shadow:inset_0_1px_0_0_rgba(255,255,255,0.30)]",
        /** Prononcé : Liseré blanc spéculaire 50% très lumineux. */
        strong: "shadow-sm [box-shadow:inset_0_1px_0_0_rgba(255,255,255,0.50)]",
      },
      size: {
        xs: "h-7 gap-1.5 px-2.5 text-xs rounded-md [&_svg]:size-3.5",
        sm: "h-8 px-3 text-xs sm:text-sm rounded-md [&_svg]:size-3.5 sm:[&_svg]:size-4",
        md: "h-9 px-3.5 text-sm rounded-lg [&_svg]:size-4",
        lg: "h-11 px-4.5 text-base rounded-xl [&_svg]:size-5",
        "icon-xs": "size-7 rounded-md p-0 [&_svg]:size-3.5",
        "icon-sm": "size-8 rounded-md p-0 [&_svg]:size-4",
        icon: "size-9 rounded-lg p-0 [&_svg]:size-4",
        "icon-lg": "size-11 rounded-xl p-0 [&_svg]:size-5",
      },
      pill: {
        true: "rounded-full",
        false: "",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    compoundVariants: [
      // --- PRIMARY
      { variant: "primary", relief: "none", className: "bg-accent" },
      { variant: "primary", relief: "subtle", className: "bg-gradient-to-b from-accent to-[color-mix(in_oklch,var(--color-accent)_95%,black)]" },
      { variant: "primary", relief: "medium", className: "bg-gradient-to-b from-accent to-[color-mix(in_oklch,var(--color-accent)_88%,black)]" },
      { variant: "primary", relief: "strong", className: "bg-gradient-to-b from-accent to-[color-mix(in_oklch,var(--color-accent)_80%,black)]" },

      // --- SECONDARY
      { variant: "secondary", relief: "none", className: "bg-surface-raised" },
      { variant: "secondary", relief: "subtle", className: "bg-gradient-to-b from-surface-raised to-surface-sunken/40" },
      { variant: "secondary", relief: "medium", className: "bg-gradient-to-b from-surface-raised to-surface-sunken/75" },
      { variant: "secondary", relief: "strong", className: "bg-gradient-to-b from-surface-raised to-surface-sunken" },

      // --- DANGER
      { variant: "danger", relief: "none", className: "bg-danger" },
      { variant: "danger", relief: "subtle", className: "bg-gradient-to-b from-danger to-[color-mix(in_oklch,var(--color-danger)_95%,black)]" },
      { variant: "danger", relief: "medium", className: "bg-gradient-to-b from-danger to-[color-mix(in_oklch,var(--color-danger)_88%,black)]" },
      { variant: "danger", relief: "strong", className: "bg-gradient-to-b from-danger to-[color-mix(in_oklch,var(--color-danger)_80%,black)]" },

      // --- GLASS
      { variant: "glass", relief: "none", className: "bg-surface-raised/75" },
      { variant: "glass", relief: "subtle", className: "bg-gradient-to-b from-surface-raised/85 to-surface-raised/60" },
      { variant: "glass", relief: "medium", className: "bg-gradient-to-b from-surface-raised/90 to-surface-raised/50" },
      { variant: "glass", relief: "strong", className: "bg-gradient-to-b from-surface-raised to-surface-raised/40" },

      // --- FLOATING
      { variant: "floating", relief: "none", className: "bg-surface-raised" },
      { variant: "floating", relief: "subtle", className: "bg-gradient-to-b from-surface-raised to-surface-sunken/40" },
      { variant: "floating", relief: "medium", className: "bg-gradient-to-b from-surface-raised to-surface-sunken/75" },
      { variant: "floating", relief: "strong", className: "bg-gradient-to-b from-surface-raised to-surface-sunken" },

      // --- OUTLINE
      { variant: "outline", relief: "none", className: "bg-surface-raised/20" },
      { variant: "outline", relief: "subtle", className: "bg-gradient-to-b from-surface-raised/40 to-surface-sunken/20" },
      { variant: "outline", relief: "medium", className: "bg-gradient-to-b from-surface-raised/60 to-surface-sunken/40" },
      { variant: "outline", relief: "strong", className: "bg-gradient-to-b from-surface-raised/80 to-surface-sunken/60" },
    ],
    defaultVariants: {
      variant: "primary",
      relief: "subtle",
      size: "md",
      fullWidth: false,
    },
  },
);

export interface ButtonProps
  extends ComponentPropsWithRef<"button">,
    VariantProps<typeof buttonVariants> {
  /** Affiche un indicateur d'attente et neutralise le bouton. */
  loading?: boolean;
  /** Élément affiché avant le libellé (animé automatiquement au survol). */
  leadingIcon?: ReactNode;
  /** Élément affiché après le libellé (animé automatiquement au survol). */
  trailingIcon?: ReactNode;
  /** Badge ou compteur affiché dans le bouton (ex: notification, status dot). */
  badge?: ReactNode;
}

/**
 * Bouton Natif SOTA — composant de référence du design system.
 *
 * @example
 * <Button variant="primary" relief="subtle" size="lg" onClick={save}>Enregistrer</Button>
 * <Button variant="secondary" leadingIcon={<PlusIcon />}>Bouton Système</Button>
 * <Button variant="glass" leadingIcon={<SearchIcon />}>Rechercher</Button>
 */
export function Button({
  className,
  variant,
  relief,
  size,
  pill,
  fullWidth,
  loading = false,
  leadingIcon,
  trailingIcon,
  badge,
  disabled,
  children,
  type = "button",
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cn(buttonVariants({ variant, relief, size, pill, fullWidth }), className)}
      {...props}
    >
      {loading ? (
        <Spinner size="sm" label={null} />
      ) : (
        leadingIcon && (
          <span className="flex transform-gpu transition-transform duration-normal ease-standard group-hover/button:scale-110 group-hover/button:-translate-x-0.5 group-hover/button:will-change-transform">
            {leadingIcon}
          </span>
        )
      )}
      {children}
      {!loading && trailingIcon && (
        <span className="flex transform-gpu transition-transform duration-normal ease-standard group-hover/button:translate-x-1 group-hover/button:will-change-transform">
          {trailingIcon}
        </span>
      )}
      {badge && (
        <span className="ml-0.5 inline-flex items-center justify-center rounded-full bg-accent/15 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-accent-fg">
          {badge}
        </span>
      )}
    </button>
  );
}

export { buttonVariants };
